// =======================================================
// 1) HOTEL — Final SEO Editor (React-Bootstrap)
// File: src/views/pages/booking-engine/admin/SeoEditor.jsx
// Saves canonical SEO schema (same as restaurant).
// =======================================================

import React, { useMemo } from "react";
import { Form, Row, Col, Alert, Button, Badge, Card } from "react-bootstrap";

const ROBOTS_OPTIONS = [
  { value: "index,follow", label: "Index & Follow (default)" },
  { value: "noindex,nofollow", label: "No Index & No Follow" },
  { value: "index,nofollow", label: "Index but No Follow" },
  { value: "noindex,follow", label: "No Index but Follow" },
];

const TWITTER_CARDS = [
  { value: "summary_large_image", label: "summary_large_image" },
  { value: "summary", label: "summary" },
];

const sanitizeSlug = (v = "") =>
  String(v)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")        // ✅ spaces -> hyphen
    .replace(/[^a-z0-9-]/g, "")  // remove remaining invalid chars
    .replace(/-+/g, "-")         // collapse multiple hyphens
    .replace(/^-|-$/g, "");      // trim leading/trailing hyphens


const stripHtml = (str = "") => String(str).replace(/<[^>]*>/g, "").trim();

const isValidHttpUrl = (v = "") => /^https?:\/\/.+/i.test(String(v).trim());

/**
 * Normalize ANY historical SEO payload into the canonical schema.
 * This lets you migrate gradually without breaking older records.
 */
function normalizeSeo(seoRaw = {}, defaults = {}) {
  const seo = seoRaw && typeof seoRaw === "object" ? seoRaw : {};
  const slug = sanitizeSlug(seo.slug || defaults.slug || "");

  const title = (seo.title || seo.page_title || "").trim();
  const description = (seo.description || seo.meta_description || "").trim();
  const keywords = (seo.keywords || "").trim();

  const canonicalCandidate = (seo.canonical || "").trim();
  const canonical =
    canonicalCandidate && isValidHttpUrl(canonicalCandidate)
      ? canonicalCandidate
      : (defaults.canonical || "");

  const robots = (seo.robots || "index,follow").trim();

  // OG: support old keys og_image / ogImage / og_title / og_description
  const ogTitle = (seo.og?.title || seo.og_title || "").trim();
  const ogDescription = (seo.og?.description || seo.og_description || "").trim();
  const ogImage = (seo.og?.image || seo.og_image || seo.ogImage || "").trim();
  const ogSiteName = (seo.og?.site_name || seo.og_site_name || "NetShine ONE").trim();
  const ogType = (seo.og?.type || "website").trim();

  // Twitter: support old keys twitter_* and fallback to OG
  const twitterCard = (seo.twitter?.card || seo.twitter_card || "summary_large_image").trim();
  const twitterTitle = (seo.twitter?.title || seo.twitter_title || "").trim();
  const twitterDescription = (seo.twitter?.description || seo.twitter_description || "").trim();
  const twitterImage = (seo.twitter?.image || seo.twitter_image || "").trim();

  // Analytics: support old google_analytics_id and google_site_verification
  const ga4 = (seo.analytics?.ga4 || seo.google_analytics_id || "").trim();
  const gsc = (seo.analytics?.google_site_verification || seo.google_site_verification || "").trim();

  // Structured inputs: support old structured_data
  const schemaInput = seo.schema_input || seo.structured_data || {};
  const priceRange = (schemaInput?.price_range || "").trim();
  const starRating =
    schemaInput?.star_rating === null || schemaInput?.star_rating === undefined
      ? null
      : Number(schemaInput.star_rating);
  const reviewCount =
    schemaInput?.review_count === null || schemaInput?.review_count === undefined
      ? null
      : Number(schemaInput.review_count);

  // Assets (favicon)
  const favicon = (seo.assets?.favicon || seo.favicon || "").trim();

  return {
    slug,
    title,
    description,
    keywords,
    canonical,
    robots,

    og: {
      title: ogTitle,
      description: ogDescription,
      image: ogImage,
      site_name: ogSiteName,
      type: ogType,
    },

    twitter: {
      card: twitterCard,
      title: twitterTitle,
      description: twitterDescription,
      image: twitterImage,
    },

    analytics: {
      ga4,
      google_site_verification: gsc,
    },

    schema_input: {
      price_range: priceRange,
      star_rating: Number.isFinite(starRating) ? starRating : null,
      review_count: Number.isFinite(reviewCount) ? reviewCount : null,
    },

    assets: {
      favicon,
    },
  };
}

export default function SeoEditor({ branding, setBranding, subdomain, setSubdomain }) {
  const hotelName =
    stripHtml(branding?.propertyName) ||
    branding?.title ||
    "Your Hotel";

  const city =
    branding?.contactLocation?.city ||
    branding?.contactLocation?.City ||
    "";

  const cleanedSubdomain = sanitizeSlug(subdomain || "");
  const defaultCanonical = cleanedSubdomain ? `https://${cleanedSubdomain}.netshine.one/` : "";

  const seo = useMemo(
    () => normalizeSeo(branding?.seo || {}, { slug: cleanedSubdomain, canonical: defaultCanonical }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branding?.seo, cleanedSubdomain]
  );

  const patchSeo = (patch) => {
    setBranding((prev) => {
      const prevSeo = prev?.seo || {};
      const merged = normalizeSeo({ ...prevSeo, ...patch }, { slug: cleanedSubdomain, canonical: defaultCanonical });
      return { ...prev, seo: merged };
    });
  };

  const patchNested = (key, patch) => {
    patchSeo({ [key]: { ...(seo?.[key] || {}), ...(patch || {}) } });
  };

  // Counters
  const titleCount = (seo.title || "").length;
  const descCount = (seo.description || "").length;

  const titleBadge =
    titleCount > 120 ? "danger" : titleCount >= 111 ? "success" : "secondary";
  const descBadge =
    descCount > 160 ? "danger" : descCount >= 120 ? "success" : "secondary";

  const canonicalWarn = seo.canonical && !isValidHttpUrl(seo.canonical)
    ? "Canonical must be a full URL (https://...)."
    : "";

  const handleGenerateTitle = () => {
    if (!window.confirm("Generate SEO title using hotel name & city? This will overwrite the current title.")) return;
    const base = city ? `${hotelName} ${city}` : hotelName;
    const t = `${base} | Official Booking Site`;
    patchSeo({ title: t.slice(0, 160) });
  };

  const handleGenerateDescription = () => {
    if (!window.confirm("Generate meta description? This will overwrite the current description.")) return;
    const baseCityPart = city ? ` in ${city}` : "";
    const domainPart = cleanedSubdomain ? ` on ${cleanedSubdomain}.netshine.one` : "";
    const d = `Book direct at ${hotelName}${baseCityPart}${domainPart}. Enjoy best rates, secure booking, and exclusive offers versus OTAs.`;
    patchSeo({ description: d.slice(0, 260) });
  };

  const handleGenerateKeywords = () => {
    if (!window.confirm("Generate keywords? This will overwrite the current keywords.")) return;
    const lowerCity = (city || "").toLowerCase();
    const lowerHotel = (hotelName || "").toLowerCase();
    const list = [
      "hotel booking",
      `${lowerHotel} booking`,
      lowerCity && `${lowerCity} hotels`,
      lowerCity && `${lowerCity} resort`,
      `${lowerHotel} direct booking`,
      `${lowerHotel} best rate`,
    ].filter(Boolean);
    patchSeo({ keywords: list.join(", ") });
  };

  const handleAutoCanonical = () => {
    if (!cleanedSubdomain) return alert("Set the Custom Subdomain first.");
    patchSeo({ canonical: defaultCanonical });
  };

  const handleGenerateSocial = () => {
    if (!window.confirm("Generate OG/Twitter from Title/Description? This will overwrite existing OG/Twitter fields.")) return;
    const t = (seo.title || "").trim();
    const d = (seo.description || "").trim();
    patchNested("og", { title: t, description: d });
    patchNested("twitter", { title: t, description: d, card: seo.twitter?.card || "summary_large_image" });
  };

  return (
    <div className="pt-3">
      <Alert variant="info" className="mb-4">
        This data is saved in the <strong>seo</strong> JSONB field and used for server-rendered SEO (Option A) + public bootstrap fallback.
      </Alert>

      <Row className="gy-3">
        {/* SUBDOMAIN (kept external state, but we also mirror into seo.slug) */}
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Custom Subdomain <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={cleanedSubdomain}
              onChange={(e) => {
                const v = sanitizeSlug(e.target.value);
                setSubdomain(v);
                patchSeo({ slug: v }); // ✅ keep in seo payload too
              }}
              placeholder="e.g. divine-resort"
            />
            <small className="text-muted">
              Creates <code>https://your-subdomain.netshine.one/</code>
            </small>
          </Form.Group>
        </Col>

        {/* ROBOTS */}
        <Col md={6}>
          <Form.Group>
            <Form.Label>Robots</Form.Label>
            <Form.Select
              value={seo.robots || "index,follow"}
              onChange={(e) => patchSeo({ robots: e.target.value })}
            >
              {ROBOTS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
            <small className="text-muted">Use <strong>noindex</strong> for testing/non-public environments.</small>
          </Form.Group>
        </Col>

        {/* TITLE */}
        <Col md={12}>
          <Form.Group>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="mb-1">Page Title</Form.Label>
              <div className="d-flex gap-2 align-items-center">
                <Badge bg={titleBadge}>{titleCount}/160</Badge>
                <Button size="sm" variant="outline-secondary" onClick={handleGenerateTitle}>
                  Generate
                </Button>
              </div>
            </div>
            <Form.Control
              type="text"
              value={seo.title || ""}
              onChange={(e) => patchSeo({ title: e.target.value })}
              maxLength={160}
              placeholder="e.g. Divine Resort Rishikesh | Official Booking Site"
            />
            <small className="text-muted">Recommended 50–60 characters.</small>
          </Form.Group>
        </Col>

        {/* DESCRIPTION */}
        <Col md={12}>
          <Form.Group>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="mb-1">Meta Description</Form.Label>
              <div className="d-flex gap-2 align-items-center">
                <Badge bg={descBadge}>{descCount}/260</Badge>
                <Button size="sm" variant="outline-secondary" onClick={handleGenerateDescription}>
                  Generate
                </Button>
              </div>
            </div>
            <Form.Control
              as="textarea"
              rows={3}
              value={seo.description || ""}
              onChange={(e) => patchSeo({ description: e.target.value })}
              maxLength={260}
              placeholder="e.g. Book direct for best rates, instant confirmation, and exclusive perks vs OTAs."
            />
            <small className="text-muted">Recommended 120–260 characters.</small>
          </Form.Group>
        </Col>

        {/* KEYWORDS */}
        <Col md={12}>
          <Form.Group>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="mb-1">Keywords (optional)</Form.Label>
              <Button size="sm" variant="outline-secondary" onClick={handleGenerateKeywords}>
                Generate
              </Button>
            </div>
            <Form.Control
              type="text"
              value={seo.keywords || ""}
              onChange={(e) => patchSeo({ keywords: e.target.value })}
              placeholder="e.g. hotel booking, direct booking, rishikesh resort"
            />
            <small className="text-muted">Optional. Kept for completeness.</small>
          </Form.Group>
        </Col>

        {/* CANONICAL */}
        <Col md={12}>
          <Card className="border-0 bg-light">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Canonical URL</div>
                <Button size="sm" variant="outline-secondary" onClick={handleAutoCanonical}>
                  Auto-fill from subdomain
                </Button>
              </div>
              <Form.Control
                type="text"
                value={seo.canonical || ""}
                onChange={(e) => patchSeo({ canonical: e.target.value })}
                placeholder={defaultCanonical || "https://your-subdomain.netshine.one/"}
              />
              <small className="text-muted">
                Prevents duplicate content. {canonicalWarn && <span className="text-warning ms-1">{canonicalWarn}</span>}
              </small>
            </Card.Body>
          </Card>
        </Col>

        {/* ANALYTICS */}
        <Col md={6}>
          <Form.Group>
            <Form.Label>Google Analytics (GA4 Measurement ID)</Form.Label>
            <Form.Control
              type="text"
              value={seo.analytics?.ga4 || ""}
              onChange={(e) => patchNested("analytics", { ga4: e.target.value.trim() })}
              placeholder="G-XXXXXXXXXX"
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Google Site Verification (optional)</Form.Label>
            <Form.Control
              type="text"
              value={seo.analytics?.google_site_verification || ""}
              onChange={(e) => patchNested("analytics", { google_site_verification: e.target.value.trim() })}
              placeholder="abcdef12345..."
            />
          </Form.Group>
        </Col>

        {/* SOCIAL */}
        <Col md={12}>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <h6 className="mb-0">Social Preview (Open Graph / Twitter)</h6>
            <Button size="sm" variant="outline-secondary" onClick={handleGenerateSocial}>
              Generate from Title/Description
            </Button>
          </div>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>OG Title (optional)</Form.Label>
            <Form.Control
              type="text"
              value={seo.og?.title || ""}
              onChange={(e) => patchNested("og", { title: e.target.value })}
              maxLength={70}
              placeholder="Defaults to Page Title"
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>OG Image URL</Form.Label>
            <Form.Control
              type="text"
              value={seo.og?.image || ""}
              onChange={(e) => patchNested("og", { image: e.target.value.trim() })}
              placeholder="https://cdn.../og.jpg"
            />
            <small className="text-muted">Recommended 1200×630.</small>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group>
            <Form.Label>OG Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={seo.og?.description || ""}
              onChange={(e) => patchNested("og", { description: e.target.value })}
              maxLength={200}
              placeholder="Defaults to Meta Description"
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Twitter Card</Form.Label>
            <Form.Select
              value={seo.twitter?.card || "summary_large_image"}
              onChange={(e) => patchNested("twitter", { card: e.target.value })}
            >
              {TWITTER_CARDS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Twitter Title (optional)</Form.Label>
            <Form.Control
              type="text"
              value={seo.twitter?.title || ""}
              onChange={(e) => patchNested("twitter", { title: e.target.value })}
              maxLength={70}
              placeholder="Defaults to OG/Page title"
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Twitter Image (optional)</Form.Label>
            <Form.Control
              type="text"
              value={seo.twitter?.image || ""}
              onChange={(e) => patchNested("twitter", { image: e.target.value.trim() })}
              placeholder="Defaults to OG image"
            />
          </Form.Group>
        </Col>

        {/* FAVICON */}
        <Col md={12}>
          <Form.Group>
            <Form.Label>Favicon URL (optional)</Form.Label>
            <Form.Control
              type="text"
              value={seo.assets?.favicon || ""}
              onChange={(e) => patchNested("assets", { favicon: e.target.value.trim() })}
              placeholder="https://cdn.../favicon.png"
            />
          </Form.Group>
        </Col>

        {/* STRUCTURED INPUTS */}
        <Col md={12}>
          <h6 className="mt-4 mb-2">Structured Data Inputs (backend will convert to JSON-LD)</h6>
          <Row className="gy-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Star Rating (1–5)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={5}
                  step={0.1}
                  value={seo.schema_input?.star_rating ?? ""}
                  onChange={(e) =>
                    patchNested("schema_input", {
                      star_rating: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Review Count</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={seo.schema_input?.review_count ?? ""}
                  onChange={(e) =>
                    patchNested("schema_input", {
                      review_count: e.target.value === "" ? null : parseInt(e.target.value, 10),
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Price Range</Form.Label>
                <Form.Select
                  value={seo.schema_input?.price_range || ""}
                  onChange={(e) => patchNested("schema_input", { price_range: e.target.value })}
                >
                  <option value="">Select</option>
                  <option value="$">$ (Budget)</option>
                  <option value="$$">$$ (Moderate)</option>
                  <option value="$$$">$$$ (Luxury)</option>
                  <option value="$$$$">$$$$ (Ultra Luxury)</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Col>

        {/* PREVIEW */}
        <Col md={12}>
          <div className="mt-4 p-3 rounded border bg-light">
            <div className="fw-bold mb-1">Preview</div>
            <div style={{ fontSize: 14 }}>
              <div className="fw-semibold">{seo.title || "(no title yet)"}</div>
              <div className="text-muted">{seo.canonical || defaultCanonical || "(no canonical)"}</div>
              <div className="mt-2">{seo.description || "(no description yet)"}</div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
